import { isEqual } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'

function isEmptyValue (val: any): boolean {
  if (val === null || val === undefined || val === '') return true
  if (typeof val === 'object' && !Array.isArray(val)) {
    const keys = Object.keys(val)
    return keys.length === 0 || keys.every(k => isEmptyValue(val[k]))
  }
  return false
}

function normalizeForComparison (obj: any): any {
  if (isEmptyValue(obj)) return ''

  if (typeof obj !== 'object') {
    // Standardize numeric strings ("0") vs numbers (0)
    if (typeof obj === 'string' && !isNaN(Number(obj)) && obj.trim() !== '') {
      return Number(obj)
    }
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(normalizeForComparison).filter(item => item !== '')
  }

  return Object.keys(obj)
    .sort()
    .reduce((acc: Record<string, any>, key) => {
      const normalizedVal = normalizeForComparison(obj[key])
      // Only keep non-empty properties so newly created empty records don't trigger false positives
      if (normalizedVal !== '') {
        acc[key] = normalizedVal
      }
      return acc
    }, {})
}

export function useUnsavedChangesGuard<T> (currentData: T, ignoreElementIDs: string[] = []) {
  const baselineRef = useRef<any>(null)
  const currentDataRef = useRef<T>(currentData)
  const isMountedRef = useRef<boolean>(true)
  const ignoreElementIDsRef = useRef<string[]>(ignoreElementIDs)

  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [pendingUrl, setPendingUrl] = useState<string | null>(null)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    currentDataRef.current = currentData
  }, [currentData])

  // Keep ignoreElementIDsRef in sync with incoming prop changes
  useEffect(() => {
    ignoreElementIDsRef.current = ignoreElementIDs
  }, [ignoreElementIDs])

  // Sets the initial baseline snapshot when data is fetched
  const setBaseline = useCallback((data: T) => {
    baselineRef.current = normalizeForComparison(data)

    setTimeout(() => {
      if (isMountedRef.current) {
        setIsDirty(false)
      }
    }, 0)
  }, [])

  // Resets dirty state after a successful save
  const markClean = useCallback(() => {
    if (currentDataRef.current) {
      baselineRef.current = normalizeForComparison(currentDataRef.current)
    }

    setTimeout(() => {
      if (isMountedRef.current) {
        setIsDirty(false)
      }
    }, 0)
  }, [])

  // Track state changes against normalized baseline using deep comparison
  useEffect(() => {
    if (baselineRef.current) {
      const normalizedCurrent = normalizeForComparison(currentData)
      const hasChanged = !isEqual(normalizedCurrent, baselineRef.current)

      setIsDirty(hasChanged)
    }
  }, [currentData])

  // 1. Intercept browser window/tab closure or page refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  // 2. Intercept Browser Back/Forward navigation
  useEffect(() => {
    if (!isDirty) return

    window.history.pushState({ unsavedChangesGuard: true }, '', window.location.href)

    const handlePopState = () => {
      window.history.pushState({ unsavedChangesGuard: true }, '', window.location.href)
      setPendingUrl('BACK_NAVIGATION')
      setShowModal(true)
    }

    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [isDirty])

  // 3. Intercept in-app anchor links and GoBack buttons
  useEffect(() => {
    const handleElementClick = (e: MouseEvent) => {
      if (!isDirty) return

      const targetElement = (e.target as HTMLElement).closest('a, button') as HTMLElement | null
      if (!targetElement) return

      // Check if target element or any wrapping ancestor matches an ignored ID
      // Catches wrapper containers and nested elements.
      const isIgnored = ignoreElementIDsRef.current.some(id => {
        const ignoredElement = document.getElementById(id)
        return ignoredElement ? ignoredElement.contains(e.target as Node) : false
      })

      // Skip interception if element is explicitly ignored
      if (isIgnored) return

      if (targetElement.tagName === 'A') {
        const anchor = targetElement as HTMLAnchorElement
        if (anchor.href && !anchor.target && anchor.href !== 'javascript:void(0)') {
          e.preventDefault()
          e.stopPropagation()
          setPendingUrl(anchor.href)
          setShowModal(true)
        }
      }

      if (
        targetElement.tagName === 'BUTTON' &&
        (targetElement.classList.contains('go-back') || targetElement.closest('.go-back'))
      ) {
        e.preventDefault()
        e.stopPropagation()
        setPendingUrl('BACK_NAVIGATION')
        setShowModal(true)
      }
    }

    document.addEventListener('click', handleElementClick, true)
    return () => document.removeEventListener('click', handleElementClick, true)
  }, [isDirty])

  const handleStay = useCallback(() => {
    setShowModal(false)
    setPendingUrl(null)
  }, [])

  const handleLeave = useCallback(() => {
    setShowModal(false)
    if (pendingUrl === 'BACK_NAVIGATION') {
      window.history.go(-2)
    } else if (pendingUrl) {
      window.location.href = pendingUrl
    }
  }, [pendingUrl])

  return {
    isDirty,
    showModal,
    handleStay,
    handleLeave,
    setBaseline,
    markClean,
  }
}
